import { consola } from 'consola'
import { defineStore } from "pinia";
import { useAlertStore , modalStateEnum } from "@shared/model";
import apiAuth from '~/entities/auth/@x/user';
import apiProfileInstance from "~/entities/user/api";
import type { User } from '@/utils/types/user'
import { apiLoyaltyInstance } from '~/entities/loyalty/@x/user';


export const useUserStore = defineStore("user", () => {
  consola.debug('🔍 [user.ts] store инициализируется');
  
  useAlertStore();

  // ✅ Токены
  const authAccessToken = useCookie<string | null>("authAccessToken");
  const authRefreshToken = useCookie<string | null>("authRefreshToken");

  const isAuthModalOpen = ref(false);
  const authModalStateStore = ref(modalStateEnum.Login);
  const authModalStateOldStore = ref(null);
  const isLoggedIn = computed(() => !!authAccessToken.value);

  const setToken = (accessToken, refreshToken) => {
    authAccessToken.value = accessToken;
    authRefreshToken.value = refreshToken;
  };


  // ✅ Данные пользователя
  const user = ref<User>({
    id: 0,
    first_name: '',
    last_name: '',
    default_phone_number: '',
    default_email: '',
    birthday: new Date(),
    sex: '',
    delivery_address: [],
    is_email_verified: false,
    is_phone_verified: false,
    has_password: false,
    points: 0,
    cashback_level: '3%',
    total_spent_current_period: 0,
    points_expiry_date: null,
  });
  // ✅ Установка всех данных пользователя
  const setUser = (data: Partial<typeof user.value>) => {
    consola.debug('🔍 [user.ts] setUser вызван с данными:', data);
    consola.debug('🔍 [user.ts] setUser: текущее состояние user.value до обновления:', user.value);
    
    user.value = { ...user.value, ...data };
    
    consola.debug('✅ [user.ts] setUser: user.value обновлен:', user.value);
    consola.debug('🔍 [user.ts] setUser: проверяем основные поля:', {
      id: user.value.id,
      first_name: user.value.first_name,
      last_name: user.value.last_name,
      default_phone_number: user.value.default_phone_number,
      default_email: user.value.default_email,
    });
    
    if (data.id) {
      userId.value = data.id;
      if (process.client) {
        sessionStorage.setItem("userId", data.id);
        consola.debug('✅ [user.ts] setUser: userId сохранен в sessionStorage:', data.id);
      }
    }
  };
  const getUser = computed(() => {
    consola.debug('🔍 [user.ts] getUser computed вызван, текущее значение user.value:', user.value);
    return user.value;
  });
  // ✅ ID пользователя (храним в `sessionStorage`, чтобы не терялся при обновлении)
  const userId = ref<string | null>(null);
  if (process.client) {
    userId.value = sessionStorage.getItem("userId") || null;
  }
  const getUserId = computed(() => userId.value);

  // ✅ Данные лояльности
  const loyaltyInfo = ref({
    points: 0,
    cashbackLevel: '3%' as const,
    totalSpent: 0,
    pointsExpiryDate: null as string | null,
  });

  // ✅ Установка данных лояльности
  const setLoyaltyInfo = (data: Partial<typeof loyaltyInfo.value>) => {
    loyaltyInfo.value = { ...loyaltyInfo.value, ...data };
  };

  // ✅ Получение информации о лояльности
  const fetchLoyaltyInfo = async () => {
    try {
      if (!isLoggedIn.value) return;

      const { data } = await apiLoyaltyInstance.getLoyaltyInfo();
      setLoyaltyInfo(data);
    } catch {
      // Ошибка получения информации о баллах
    }
  };

  // ✅ Использование баллов
  const useLoyaltyPoints = async (orderId: number, pointsToUse: number) => {
    const response = await apiLoyaltyInstance.usePoints(orderId, pointsToUse);
    await fetchLoyaltyInfo(); // Обновляем баланс
    return response;
  };

  // ✅ Получение истории баллов
  const fetchLoyaltyHistory = (page = 1, limit = 10) =>
    apiLoyaltyInstance.getLoyaltyHistory(page, limit);

  const login = (params) => {
    return apiAuth
      .login(params)
      .then(({ data }) => {
        authAccessToken.value = data.accessToken;
        authRefreshToken.value = data.refreshToken;
        return data;
      })
      .then(({ data }) => {
        fetchUser();
        return data;
      })
      .catch((error) => {
        throw error;
      });

  };
  const register = (params) => {
    return apiAuth
      .register(params)
      .then(({ data }) => {
        return data;
      })
      .catch((error) => {
        throw error;
      });

  };
  const checkPhoneVerification = (params) => {
    return apiAuth
      .checkPhoneVerification(params)
      .then(({ data }) => {
        authAccessToken.value = data.accessToken;
        authRefreshToken.value = data.refreshToken;
        return data;
      })
      .then(({ data }) => {
        fetchUser();
        return data;
      })
      .catch((error) => {
        throw error;
      });

  };
  // ✅ Выход пользователя (очистка токенов)
  // ⚠️ DEPRECATED: Используйте useAuthStore().logout() вместо этого
  const logout = async () => {
    consola.warn('[user.ts] ⚠️ DEPRECATED: Используйте useAuthStore().logout()')
    
    try {
      await apiAuth.logout({id: getUserId.value})
    } catch {
      consola.warn('[user.ts] Ошибка API logout, продолжаем разлогинивание')
    }
    
    // Используем централизованный сервис
    const { AuthLogoutService, LogoutReason, UserType } = await import('~/entities/auth/@x/user')
    await AuthLogoutService.logout({
      reason: LogoutReason.USER_INITIATED,
      userType: UserType.REGULAR,
      metadata: {
        source: 'stores/user.ts (legacy)',
        userId: getUserId.value,
      },
    })
  };

  const sendVerificationEmail = ((email: string, institutionId: number) => {
    return apiAuth
      .sendVerificationEmail({ email , institutionId})
      .then((data) => {
        return data
      })
      .catch((error) => {
        throw error;
      });
  });

  // ✅ Обновление `accessToken` через API
  const refreshAccessToken = () => {
    if (!authRefreshToken.value) {
      logout();
      return;
    }

    return apiAuth
      .tokenRefresh({ refreshToken: authRefreshToken.value })
      .then(({ data }) => {
        if (data.accessToken) {
          authAccessToken.value = data.accessToken;
          return data.accessToken;
        } else {
          logout();
          return null;
        }
      })
      .catch((_error) => {
        logout();
        return null;
      });
  };

  // ✅ Проверка `accessToken` перед `fetchUser()`
  const ensureValidToken = () => {
    if (!authAccessToken.value) return Promise.resolve(false);

    return apiProfileInstance
      .getUser()  // ✅ Запрашиваем данные сразу
      .then(({ status, data }) => {
        if (status === 200) {
          setUser(data); // ✅ Устанавливаем пользователя сразу
          return true;   // ✅ Токен валиден
        }
        return false;
      })
      .catch(({ status }) => {
        if (status === 403) {
          return refreshAccessToken()
            .then((newToken) => {
              if (newToken) {
                return apiProfileInstance.getUser()
                  .then(({ status, data }) => {
                    if (status === 200) {
                      setUser(data); // ✅ Сохраняем пользователя
                      return true;
                    }
                    logout(); // Немедленный разлогин при неудачном получении данных
                    return false;
                  });
              } else {
                logout(); // Немедленный разлогин при неудачном обновлении токена
                return false;
              }
            })
            .catch(() => {
              logout(); // Немедленный разлогин при ошибке
              return false;
            });
        }
        return false;
      });
  };

  const fetchUser = () => {
    if (!isLoggedIn.value) return;

    ensureValidToken()
      .catch((_error) => {/* Ошибка проверки токена */});
  };


  // ✅ Авторизация через Яндекс
  const tokenYandex = (params: any) => {
    return apiAuth
      .tokenYandex(params)
      .then(({ data }) => {
        authAccessToken.value = data.access_token;
        authRefreshToken.value = data.refresh_token;
        return fetchUser();
      })
      .catch((error) => {
        throw error;
      });
  };

  // ✅ Модальное окно авторизации
  const openAuthModal = (state: any) => {
    isAuthModalOpen.value = true;
    authModalStateStore.value = state;
  };

  const closeAuthModal = () => {
    isAuthModalOpen.value = false;
    authModalStateStore.value = modalStateEnum.Login;
  };

  const stateAuthModal = (state) => {
    authModalStateStore.value = state;
  };
  // ✅ Слежение за состоянием модального окна
  watch(() => authModalStateStore.value, (_stateN, stateO) => {
      authModalStateOldStore.value = stateO;
    });

  // ✅ Автоматически проверяем `auth` и загружаем пользователя при монтировании
  // onBeforeMount(() => {
  //   fetchUser();
  // });

  return {
    isLoggedIn,
    user,
    setUser,
    getUser,
    fetchUser,
    setToken,
    register,
    checkPhoneVerification,
    login,
    logout,
    tokenYandex,
    openAuthModal,
    closeAuthModal,
    authModalStateStore,
    authModalStateOldStore,
    isAuthModalOpen,
    stateAuthModal,
    sendVerificationEmail,
    loyaltyInfo,
    fetchLoyaltyInfo,
    useLoyaltyPoints,
    fetchLoyaltyHistory,
    setLoyaltyInfo,
  };
});
